import { DashboardState, ESdChangeReason, IDatabaseColumnModel } from "@kuika/kuika-cl-model";
import {
  ECardinality,
  EManagedDbSaveMethod,
  EManagedDbUpdateWhereConditionOperator,
  EManagedDbUpdateWhereType,
  IAction,
  IActionParameter,
  IManagedDbDataModel,
  IManagedDbSaveModel,
  IManagedDbWhereConditionModel,
  IManagedDbWhereModel,
  ISymbol
} from "@kuika/kuika-cl-model/src/models/builder.dtos";
import { Fragment } from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import {
  formatQualifiedName,
  getTableDatasourceQualifiers
} from "../../../../shared/utility/datasource-name-qualifier";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import ActionParameterTitle from "./action-parameter-title";
import {
  findSelectedManagedDbTable,
  IManagedDbTable,
  IManagedDbTableSelection,
  isSelectedManagedDbTable
} from "./managed-db-table-selection";
import { ManagedDbWhereCondition } from "./managed-db-where-condition-panel";
import { SymbolPicker } from "./symbol-picker";

const SAVE_METHOD_OPTIONS: KuikaSelectboxOption[] = [
  { key: "smart", value: EManagedDbSaveMethod.smart, label: "Smart", testId: "smartSaveRecordMethodOption" },
  { key: "insert", value: EManagedDbSaveMethod.insert, label: "Insert", testId: "insertSaveRecordMethodOption" },
  { key: "update", value: EManagedDbSaveMethod.update, label: "Update", testId: "updateSaveRecordMethodOption" }
];

export interface IManagedDbSaveSymbolGroupState {
  isColumnSelectDropdownOpen: boolean;
  selectedColumns: string[];
}

export interface IManagedDbSaveSymbolGroupProps {
  action: IAction;
  managedDbData?: IManagedDbDataModel;
  updateManagedDbData: (managedDbData: IManagedDbDataModel, changeReason?: ESdChangeReason) => void;
}

interface ITable {
  id?: string;
  key: string;
  title: string;
  schema: string;
  columns: IDatabaseColumnModel[];
  dropped: boolean;
  dsId: string;
  hasIdColumn: boolean;
}

/** Save Record's name comparison, as it always was; the datasource is settled by the caller. */
function isSaveTableNameMatch(table: IManagedDbTable, selection: IManagedDbTableSelection): boolean {
  return table.title == selection.selectedTableName && table.schema == selection.selectedTableSchema;
}

class ManagedDbSaveSymbolGroup extends PureSdComponent<
  IManagedDbSaveSymbolGroupProps & IWithSDProps,
  IManagedDbSaveSymbolGroupState
> {
  constructor(props: IManagedDbSaveSymbolGroupProps & IWithSDProps) {
    super(props);
    this.state = {
      isColumnSelectDropdownOpen: false,
      selectedColumns: props?.managedDbData?.save?.parameters?.map((x) => x.name) ?? []
    };
  }

  componentDidMount = () => {
    if (!this.props.managedDbData?.save) {
      const emptyManagedDbData: IManagedDbSaveModel = {
        method: EManagedDbSaveMethod.smart,
        selectedTableName: undefined,
        selectedDatasourceId: undefined,
        parameters: []
      };
      this.updateManagedDbSaveData(emptyManagedDbData);
    } else {
      this.updateSaveDataIfNeeded();
    }
  };

  updateSaveDataIfNeeded = () => {
    const tables = this.getTables();
    const saveData = this.props.managedDbData?.save;
    if (!saveData || !tables || tables.length === 0) return;
    let paramAdded: boolean = false;
    let tableNameChanged: boolean = false;

    const selectedTable: ITable | undefined = findSelectedManagedDbTable(tables, saveData, isSaveTableNameMatch);

    if (
      selectedTable &&
      (saveData.method === EManagedDbSaveMethod.insert || saveData.method === EManagedDbSaveMethod.smart)
    ) {
      const requiredCols = this.getFilteredColumns(selectedTable?.columns).filter((x) => this.isRequiredCol(x));
      requiredCols.forEach((requiredCol) => {
        if (!saveData.parameters.find((x) => x.name === requiredCol.name)) {
          const parameter = this.getEmptyParameterForCol(
            requiredCol.name,
            requiredCol.sqlDataType,
            requiredCol.maxLength
          );
          saveData.parameters.push(parameter);
          paramAdded = true;
        }
      });
    }

    if (saveData.selectedTableId) {
      const table: ITable | undefined = tables.find((x) => x.id === saveData.selectedTableId);
      const tableName: string | undefined = table?.title;

      if (table && tableName && tableName?.length > 0) {
        const newTableName: string = `${table.schema}.${tableName}`;

        if (saveData.selectedTableName !== newTableName) {
          saveData.selectedTableName = newTableName;
          saveData.selectedTableSchema = `${table.schema}`;
          tableNameChanged = true;
        }
      }
    }

    if (paramAdded || tableNameChanged) {
      this.updateManagedDbSaveData(saveData);
    }
  };

  getEmptyWhereCondition = (): IManagedDbWhereModel => {
    return {
      type: EManagedDbUpdateWhereType.and,
      andCondition: {
        colName: "Id",
        conditionOperator: EManagedDbUpdateWhereConditionOperator.isEqualTo,
        symbol: KMainFunctions.getDefaultSymbol("Guid"),
        typeName: "Guid"
      }
    };
  };

  updateManagedDbSaveData = (saveData: IManagedDbSaveModel) => {
    const managedDbData: IManagedDbDataModel = {
      save: saveData
    };
    this.props.updateManagedDbData(managedDbData);
  };

  handleSelectMethod = (method: EManagedDbSaveMethod, selectedTable: ITable | undefined): void => {
    const saveData = this.props.managedDbData?.save;
    if (!saveData) return;

    if (saveData.method !== EManagedDbSaveMethod.update && method === EManagedDbSaveMethod.update) {
      const where = this.getEmptyWhereCondition();
      saveData.where = [where];
      saveData.parameters = saveData.parameters.filter((x) => x.name !== "Id");
    } else if (saveData.method === EManagedDbSaveMethod.update && method !== EManagedDbSaveMethod.update) {
      if (saveData.where) delete saveData.where;
    }

    saveData.method = method;
    if (selectedTable && (method === EManagedDbSaveMethod.insert || method === EManagedDbSaveMethod.smart)) {
      const requiredCols = this.getFilteredColumns(selectedTable?.columns).filter((x) => this.isRequiredCol(x));
      requiredCols.forEach((requiredCol) => {
        if (!saveData.parameters.find((x) => x.name === requiredCol.name)) {
          // add required columns..
          const parameter = this.getEmptyParameterForCol(
            requiredCol.name,
            requiredCol.sqlDataType,
            requiredCol.maxLength
          );
          saveData.parameters.push(parameter);
        }
      });
    }
    this.updateManagedDbSaveData(saveData);
  };

  handleSelectTable = (key: string): void => {
    const tables = this.getTables();
    const selectedTable = tables.find((x) => x.key === key);
    const saveData = this.props.managedDbData?.save;
    // Same table only when the current selection resolves to this very entry: a same-named table of
    // another datasource is a different table, and picking it must switch (KOA-18147).
    if (
      !saveData ||
      !selectedTable ||
      isSelectedManagedDbTable(tables, saveData, selectedTable, isSaveTableNameMatch)
    ) {
      return;
    }

    const emptyManagedDbData: IManagedDbSaveModel = {
      method: saveData.method,
      selectedTableName: selectedTable?.title,
      selectedTableSchema: selectedTable?.schema,
      selectedTableId: selectedTable?.id,
      selectedDatasourceId: selectedTable?.dsId,
      parameters: []
    };
    this.getFilteredColumns(selectedTable?.columns)
      .filter((x) => this.isRequiredCol(x))
      .map((x) => x.name)
      .forEach((col) => {
        if (saveData.method === EManagedDbSaveMethod.update && col === "Id") {
          return;
        }
        const dbColumn = this.getFilteredColumns(selectedTable?.columns).find((x) => x.name === col);
        if (dbColumn) {
          const parameter = this.getEmptyParameterForCol(col, dbColumn.sqlDataType, dbColumn.maxLength);
          emptyManagedDbData.parameters.push(parameter);
        }
      });

    if (saveData.method === EManagedDbSaveMethod.update) {
      const where = this.getEmptyWhereCondition();
      emptyManagedDbData.where = [where];
    }

    this.updateManagedDbSaveData(emptyManagedDbData);
  };

  handleSelectIdColumn = (selectedIdColumnName: string): void => {
    const saveData = this.props.managedDbData?.save;
    if (!saveData) return;
    saveData.idColName = selectedIdColumnName;

    // add to columns if not exists
    if (!saveData.parameters.find((x) => x.name === selectedIdColumnName)) {
      const tables = this.getTables();
      const selectedTable: ITable | undefined = findSelectedManagedDbTable(tables, saveData, isSaveTableNameMatch);
      const selectedTableColumns = this.getFilteredColumns(selectedTable?.columns);
      const dbColumn = selectedTableColumns?.find((x) => x.name === selectedIdColumnName);
      if (dbColumn) {
        const parameter = this.getEmptyParameterForCol(dbColumn.name, dbColumn.sqlDataType, dbColumn.maxLength);
        saveData.parameters.push(parameter);
      }
    }
    this.updateManagedDbSaveData(saveData);
  };

  handleSelectColumns = (selectedColumns: string[]): void => {
    this.setState({ selectedColumns });
  };

  handleDropdownVisibleChange = (isOpen: boolean): void => {
    this.setState(
      {
        isColumnSelectDropdownOpen: isOpen
      },
      () => {
        if (!isOpen) this.updateSelectedColumns();
      }
    );
  };

  // A deselect made while the list is open is already carried by `onChange`, which reports the
  // whole remaining selection; this is for the one the list cannot report that way -- a tag
  // removed from the collapsed field -- and it persists as well as deselects.
  handleDeselect = (value: string | number | null): void => {
    if (this.state.isColumnSelectDropdownOpen) return;
    const newSelectedColumns: string[] = this.state.selectedColumns.filter((x: string): boolean => x !== value);
    this.setState(
      {
        selectedColumns: newSelectedColumns
      },
      this.updateSelectedColumns
    );
  };

  updateSelectedColumns = () => {
    const selectedColumns = this.state.selectedColumns;
    const saveData = this.props.managedDbData?.save;
    if (!saveData) return;
    const notFilteredColumns = this.props.sd.schemas
      .get(saveData.selectedDatasourceId ?? "")
      ?.tables.find(
        (x) =>
          (x.id != null && x.id?.toLowerCase() === saveData.selectedTableId?.toLowerCase()) ||
          (x.title == saveData.selectedTableName && x.schema == saveData.selectedTableSchema)
      )?.columns;
    const selectedTableColumns = this.getFilteredColumns(notFilteredColumns);
    const requiredColumns = notFilteredColumns?.filter((x) => this.isColumnDisabled(x, saveData.method));
    const newParameters: IActionParameter[] = [];

    requiredColumns?.forEach((col) => {
      const dbColumn = selectedTableColumns?.find((x) => x.name === col.name);
      if (dbColumn) {
        if (selectedColumns.indexOf(col.name) == -1) {
          selectedColumns.push(col.name);
        }
      }
    });

    selectedColumns.forEach((colName) => {
      const existingParam = saveData.parameters.find((x) => x.name === colName);

      if (newParameters.filter((x) => x.name == existingParam?.name).length == 0) {
        if (existingParam) {
          newParameters.push(existingParam);
        } else {
          const dbColumn = selectedTableColumns?.find((x) => x.name === colName);
          if (dbColumn) {
            const parameter = this.getEmptyParameterForCol(dbColumn.name, dbColumn.sqlDataType, dbColumn.maxLength);
            newParameters.push(parameter);
          }
        }
      }
    });

    this.handleSelectColumns(selectedColumns);

    saveData.parameters = newParameters;
    this.updateManagedDbSaveData(saveData);
  };

  getEmptyParameterForCol = (
    colName: string,
    sqlDataType: string,
    maxLength: number | undefined | null
  ): IActionParameter => {
    let typeName = KMainFunctions.getTypeNameBySqlDataType(sqlDataType);
    if (typeName == "Unknown") {
      typeName = KMainFunctions.getTypeNameBySqlDataType(`${sqlDataType}(${maxLength})`);
    }
    const parameter: IActionParameter = {
      name: colName,
      symbol: KMainFunctions.getDefaultSymbol(typeName),
      Typename: typeName,
      cardinality: ECardinality.One
    };
    return parameter;
  };

  handleSelectSymbol(parameter: IActionParameter, symbol: ISymbol, changeReason: ESdChangeReason | undefined): void {
    const saveData = this.props.managedDbData?.save;
    const paramToUpdate = saveData?.parameters.find((x) => x.name === parameter.name);
    if (saveData && paramToUpdate) {
      paramToUpdate.symbol = symbol;
      this.updateManagedDbSaveData(saveData);
    }
  }

  getTables = (): ITable[] => {
    const result: ITable[] = [];
    const { schemas } = this.props.sd;
    const dsIdsProcessed: string[] = [];
    const defaultKuikaTableNames = KMainFunctions.getDefaultKuikaTableNames();

    schemas?.forEach((schema) => {
      if (dsIdsProcessed.indexOf(schema.datasourceId) != -1) return;
      dsIdsProcessed.push(schema.datasourceId);
      schema.tables.forEach((table) => {
        if (defaultKuikaTableNames.indexOf(table.title) === -1) {
          const hasIdColumn = table.columns.find((x) => x.name.toLowerCase() === "id") !== undefined;
          result.push({ ...table, dsId: schema.datasourceId, hasIdColumn });
        }
      });
    });

    return result;
  };

  isColumnDisabled = (column: any, saveMethod?: EManagedDbSaveMethod): boolean => {
    if (saveMethod === EManagedDbSaveMethod.smart || saveMethod === EManagedDbSaveMethod.insert) {
      return this.isRequiredCol(column);
    }
    return false;
  };

  handleAddAnd = () => {
    const saveData = this.props.managedDbData?.save;
    if (!saveData) return;
    if (!saveData?.where) saveData.where = [];
    saveData.where.push({
      type: EManagedDbUpdateWhereType.and,
      andCondition: {
        conditionOperator: EManagedDbUpdateWhereConditionOperator.isEqualTo,
        symbol: KMainFunctions.getDefaultSymbol("Unknown"),
        typeName: "Unknown"
      }
    });
    this.updateManagedDbSaveData(saveData);
  };

  handleAddAndToOrGroup = (idx: number) => {
    const saveData = this.props.managedDbData?.save;
    if (!saveData) return;
    if (!saveData?.where) saveData.where = [];
    saveData.where[idx].orConditions?.push({
      conditionOperator: EManagedDbUpdateWhereConditionOperator.isEqualTo,
      symbol: KMainFunctions.getDefaultSymbol("Unknown"),
      typeName: "Unknown"
    });
    this.updateManagedDbSaveData(saveData);
  };

  handleAddOrGroup = () => {
    const saveData = this.props.managedDbData?.save;
    if (!saveData) return;
    if (!saveData?.where) saveData.where = [];
    saveData.where.push({
      type: EManagedDbUpdateWhereType.orGroup,
      orConditions: [
        {
          conditionOperator: EManagedDbUpdateWhereConditionOperator.isEqualTo,
          symbol: KMainFunctions.getDefaultSymbol("Unknown"),
          typeName: "Unknown"
        }
      ]
    });
    this.updateManagedDbSaveData(saveData);
  };

  updateAndConditions = (updatedCondition: IManagedDbWhereConditionModel, idx: number) => {
    const saveData = this.props.managedDbData?.save;
    if (!saveData || !saveData.where) return;
    saveData.where[idx].andCondition = updatedCondition;
    this.updateManagedDbSaveData(saveData);
  };

  removeAndCondition = (idx: number) => {
    const saveData = this.props.managedDbData?.save;
    if (!saveData || !saveData.where) return;
    saveData.where.splice(idx, 1);
    this.updateManagedDbSaveData(saveData);
  };

  updateOrCondition = (updatedCondition: IManagedDbWhereConditionModel, idx: number, orIdx: number) => {
    const saveData = this.props.managedDbData?.save;
    if (!saveData || !saveData.where) return;
    saveData.where[idx].orConditions![orIdx] = updatedCondition;
    this.updateManagedDbSaveData(saveData);
  };

  removeOrCondition = (idx: number, orIdx: number) => {
    const saveData = this.props.managedDbData?.save;
    if (!saveData || !saveData.where) return;
    if (saveData.where[idx].orConditions?.length === 1) {
      saveData.where.splice(idx, 1);
    } else {
      saveData.where[idx].orConditions!.splice(orIdx, 1);
    }
    this.updateManagedDbSaveData(saveData);
  };

  getFilteredColumns = (columns: IDatabaseColumnModel[] | undefined): IDatabaseColumnModel[] => {
    if (!columns) return [];
    return columns;
  };

  isRequiredCol = (col: IDatabaseColumnModel): boolean => {
    if (
      !col.isAutoIncrement &&
      col.nullable === false &&
      (col.defaultValue === null || col.defaultValue?.length === 0)
    ) {
      return true;
    }

    const saveData = this.props.managedDbData?.save;
    if (saveData?.idColName === col.name) {
      return true;
    }

    return false;
  };

  handleRedirectToDatasources = () => {
    this.props.sd.setDashboardState(DashboardState.dataDiagramScheme);
  };

  render() {
    const tables = this.getTables();
    const saveData = this.props.managedDbData?.save;
    if (!saveData || !tables || tables.length === 0)
      return (
        <span style={{ fontSize: 10 }}>
          You don't have any tables at the moment. Please{" "}
          <a onClick={this.handleRedirectToDatasources}>create a table</a> before to be able to use SaveRecord.
        </span>
      );
    const selectedTable: ITable | undefined = findSelectedManagedDbTable(tables, saveData, isSaveTableNameMatch);
    const selectedTableColumns = this.getFilteredColumns(selectedTable?.columns);
    // A table name another datasource also holds is shown as "[CRM_DB] Users" (KOA-18147).
    const tableQualifiers: Map<string, string> = getTableDatasourceQualifiers(tables, this.props.sd.datasources);
    const tableOptions: KuikaSelectboxOption[] = tables.map((table: ITable): KuikaSelectboxOption => ({
      key: table.key,
      value: table.key,
      label: formatQualifiedName(table.title, tableQualifiers.get(table.key))
    }));
    const idColumnOptions: KuikaSelectboxOption[] = (selectedTable?.columns ?? []).map(
      (column: IDatabaseColumnModel): KuikaSelectboxOption => ({
        key: column.name,
        value: column.name,
        label: column.name
      })
    );
    const columnOptions: KuikaSelectboxOption[] = selectedTableColumns.map(
      (column: IDatabaseColumnModel): KuikaSelectboxOption => ({
        key: column.name,
        value: column.name,
        label: column.name,
        disabled: this.isColumnDisabled(column, saveData.method)
      })
    );

    return (
      <div style={{ marginTop: 12, marginBottom: 12 }}>
        <div className="kuika_symbol-picker__managed-db">
          <KuikaSelectbox
            placeholder="Select Method"
            className="kuika__action-parameters__item-select"
            value={saveData.method}
            onChange={(method: EManagedDbSaveMethod): void => this.handleSelectMethod(method, selectedTable)}
            dropdownMatchSelectWidth={false}
            options={SAVE_METHOD_OPTIONS}
          />

          <KuikaSelectbox
            searchable
            placeholder="Select Table"
            data-testid="saveRecordTableSelect"
            className="kuika__action-parameters__item-select"
            value={selectedTable?.key ?? null}
            onChange={this.handleSelectTable}
            dropdownMatchSelectWidth={false}
            options={tableOptions}
          />

          {selectedTable && (
            <>
              {!selectedTable.hasIdColumn && (
                <KuikaSelectbox
                  searchable
                  placeholder="Select ID Column"
                  className="kuika__action-parameters__item-select"
                  onChange={this.handleSelectIdColumn}
                  dropdownMatchSelectWidth={false}
                  value={saveData.idColName ?? null}
                  options={idColumnOptions}
                />
              )}
              <KuikaSelectbox
                searchable
                mode="multiple"
                placeholder="Select Columns"
                data-testid="saveRecordColumnsSelect"
                className="kuika__action-parameters__item-select"
                onChange={this.handleSelectColumns}
                dropdownMatchSelectWidth={false}
                value={this.state.selectedColumns}
                visible={this.state.isColumnSelectDropdownOpen}
                onVisibleChange={this.handleDropdownVisibleChange}
                onDeselect={this.handleDeselect}
                options={columnOptions}
              />
            </>
          )}

          <div className="kuika_property-grid-column">
            {saveData.parameters.map((parameter, pIdx) => {
              return (
                <div key={pIdx}>
                  <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
                    <ActionParameterTitle parameter={parameter} />
                  </span>
                  {
                    <SymbolPicker
                      parameter={parameter}
                      action={this.props.action}
                      changeType="onBlur"
                      symbol={parameter.symbol}
                      valueType={parameter.Typename}
                      targetCardinality={parameter.cardinality}
                      allowConditionValue
                      onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) =>
                        this.handleSelectSymbol(parameter, symbol, changeReason)
                      }
                    />
                  }
                </div>
              );
            })}
          </div>

          {selectedTable && saveData?.method === EManagedDbSaveMethod.update && (
            <>
              <div style={{ padding: 3 }} className="kuika_managed-db-save-symbol-group">
                <div style={{ background: "var(--dark-0)", padding: 4 }}>
                  <span className="kuika_managed-db-save-symbol-group__where-label">WHERE</span>
                  {saveData.where?.map((item, idx) => {
                    return (
                      <Fragment key={idx}>
                        {idx !== 0 && (
                          <div style={{ margin: "8px 4px" }}>
                            <span style={{ background: "var(--dark-2)", borderRadius: "var(--radius-xs)", padding: 4 }}>
                              AND
                            </span>
                          </div>
                        )}
                        <div key={idx} style={{ background: "var(--blue-05)", padding: 6 }}>
                          {item.type === EManagedDbUpdateWhereType.and && item.andCondition && (
                            <>
                              <ManagedDbWhereCondition
                                action={this.props.action}
                                conditionModel={item.andCondition}
                                tableColumns={selectedTableColumns}
                                updateConditionModel={(updatedCondition: IManagedDbWhereConditionModel) =>
                                  this.updateAndConditions(updatedCondition, idx)
                                }
                                removeCondition={() => this.removeAndCondition(idx)}
                              />
                            </>
                          )}
                          {item.type === EManagedDbUpdateWhereType.orGroup && (
                            <div>
                              <div>
                                <span style={{ marginRight: 6, color: "var(--dark-6)" }}>OR GROUP</span>
                              </div>
                              {item.orConditions?.map((orCondition, orIdx) => {
                                return (
                                  <Fragment key={orIdx}>
                                    {orIdx !== 0 && (
                                      <div style={{ margin: "8px 4px" }}>
                                        <span
                                          style={{
                                            background: "var(--dark-2)",
                                            borderRadius: "var(--radius-xs)",
                                            padding: 4
                                          }}
                                        >
                                          OR
                                        </span>
                                      </div>
                                    )}

                                    <ManagedDbWhereCondition
                                      action={this.props.action}
                                      conditionModel={orCondition}
                                      tableColumns={selectedTableColumns}
                                      updateConditionModel={(updatedCondition: IManagedDbWhereConditionModel) =>
                                        this.updateOrCondition(updatedCondition, idx, orIdx)
                                      }
                                      removeCondition={() => this.removeOrCondition(idx, orIdx)}
                                    />
                                  </Fragment>
                                );
                              })}

                              <span
                                className="kuika_addNavigationLink"
                                onClick={(e) => this.handleAddAndToOrGroup(idx)}
                              >
                                + OR
                              </span>
                            </div>
                          )}
                        </div>
                      </Fragment>
                    );
                  })}
                  <div>
                    <span
                      className="kuika_addNavigationLink"
                      onClick={this.handleAddAnd}
                      style={{ display: "inline-block" }}
                    >
                      + AND
                    </span>
                    <span
                      className="kuika_addNavigationLink"
                      onClick={this.handleAddOrGroup}
                      style={{ display: "inline-block", float: "right", paddingRight: 6 }}
                    >
                      + OR GROUP
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.WFStepMappingChange ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock
    ) {
      return true;
    }
    return false;
  };
}

const managedDbSaveSymbolGroup = withSD<IManagedDbSaveSymbolGroupProps>(ManagedDbSaveSymbolGroup);
export { managedDbSaveSymbolGroup as ManagedDbSaveSymbolGroup };
